/**
 * MyLeadsTab
 * Full agent leads management functionality embedded in dashboard tab
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import api from '../../../services/api';
import Button from '../../common/Button';
import Spinner from '../../common/Spinner';
import Modal from '../../common/Modal';
import LeadTable from '../../leads/LeadTable';
import LeadCardView from '../../leads/LeadCardView';
import LeadFilters from '../../leads/LeadFilters';
import LeadDetailPane from '../../leads/LeadDetailPane';
import { toast } from 'react-toastify';

export default function MyLeadsTab() {
  const { user: currentUser } = useAuth();
  const navigate = useNavigate();
  const [assignments, setAssignments] = useState([]);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLead, setSelectedLead] = useState(null);
  const [showDetailPane, setShowDetailPane] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingLead, setEditingLead] = useState(null);
  const [editFormData, setEditFormData] = useState({
    name: '',
    phoneNumber: '',
    email: '',
    propertyType: '',
    projectType: '',
    platform: '',
    campaign_name: '',
    adset_name: '',
    ad_name: '',
    roomCount: '',
    squareFootage: '',
    budgetMin: '',
    budgetMax: '',
    preferredStyle: '',
    roomTypes: '',
    address: '',
    timeframe: '',
    initialNotes: ''
  });
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createFormData, setCreateFormData] = useState({
    name: '',
    phoneNumber: '',
    email: '',
    propertyType: 'condo',
    projectType: 'full_renovation',
    platform: 'manual',
    campaign_name: '',
    adset_name: '',
    ad_name: '',
    roomCount: '',
    squareFootage: '',
    budgetMin: '',
    budgetMax: '',
    preferredStyle: '',
    roomTypes: '',
    address: '',
    timeframe: '',
    initialNotes: ''
  });

  // View and sorting state
  const [viewMode, setViewMode] = useState('cards'); // Default to cards for agents
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('DESC');

  // Enhanced filters
  const [filters, setFilters] = useState({
    status: '',
    propertyType: '',
    projectType: '',
    platform: '',
    search: '',
    heatLevel: '',
    dateFrom: '',
    dateTo: ''
  });

  // Fetch assignments on mount
  useEffect(() => {
    fetchAssignments();
  }, []);

  // Re-fetch when filters or sorting changes
  useEffect(() => {
    fetchAssignments();
  }, [filters, sortBy, sortOrder]);

  async function fetchAssignments() {
    try {
      setLoading(true);
      const params = {
        ...filters,
        sortBy,
        sortOrder
      };
      const response = await api.get('/leads/my/assignments', { params });

      // Transform assignments to leads format for new components
      const transformedLeads = response.data.assignments.map(assignment => ({
        id: assignment.lead.id,
        name: assignment.lead.name,
        phoneNumber: assignment.lead.phoneNumber,
        email: assignment.lead.email,
        propertyType: assignment.lead.propertyType,
        projectType: assignment.lead.projectType,
        budgetRange: assignment.lead.budgetRange,
        platform: assignment.lead.platform,
        createdAt: assignment.lead.createdAt,
        updatedAt: assignment.lead.updatedAt,
        // Include assignment data
        assignments: [assignment]
      }));

      setAssignments(response.data.assignments);
      setLeads(transformedLeads);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to fetch assignments');
    } finally {
      setLoading(false);
    }
  }

  function handleSort(field, order) {
    setSortBy(field);
    setSortOrder(order);
  }

  async function handleHeatLevelChange(assignmentId, level) {
    try {
      await api.patch(`/leads/assignments/${assignmentId}/status`, {
        heatLevel: level
      });
      toast.success('Heat level updated');

      // Update local state instead of full refresh
      setLeads(prevLeads => prevLeads.map(lead => {
        const assignment = lead.assignments?.find(a => a.id === assignmentId);
        if (assignment) {
          assignment.heatLevel = level;
        }
        return lead;
      }));

      // Update selectedLead if it's the one being modified
      if (selectedLead) {
        const assignment = selectedLead.assignments?.find(a => a.id === assignmentId);
        if (assignment) {
          setSelectedLead({ ...selectedLead });
        }
      }
    } catch (error) {
      toast.error('Failed to update heat level');
    }
  }

  function handleViewLead(lead) {
    setSelectedLead(lead);
    setShowDetailPane(true);
  }

  function handleEditLead(lead) {
    setEditingLead(lead);
    setEditFormData({
      name: lead.name || '',
      phoneNumber: lead.phoneNumber || '',
      email: lead.email || '',
      propertyType: lead.propertyType || '',
      projectType: lead.projectType || '',
      platform: lead.platform || '',
      campaign_name: lead.campaign_name || '',
      adset_name: lead.adset_name || '',
      ad_name: lead.ad_name || '',
      roomCount: lead.roomCount || '',
      squareFootage: lead.squareFootage || '',
      budgetMin: lead.budgetMin || '',
      budgetMax: lead.budgetMax || '',
      preferredStyle: lead.preferredStyle || '',
      roomTypes: lead.roomTypes || '',
      address: lead.address || '',
      timeframe: lead.timeframe || '',
      initialNotes: lead.initialNotes || ''
    });
    setShowEditModal(true);
    setShowDetailPane(false);
  }

  async function handleSaveEdit() {
    if (!editingLead) return;

    try {
      await api.patch(`/leads/${editingLead.id}`, editFormData);
      toast.success('Lead updated successfully');
      setShowEditModal(false);
      setEditingLead(null);
      fetchAssignments(); // Refresh the list
    } catch (error) {
      console.error('Failed to update lead:', error);
      toast.error(error.response?.data?.error || 'Failed to update lead');
    }
  }

  async function handleCreateLead() {
    // Validate required fields
    if (!createFormData.name || !createFormData.phoneNumber) {
      toast.error('Name and phone number are required');
      return;
    }

    try {
      const response = await api.post('/leads', createFormData);
      toast.success('Lead created successfully!');
      setShowCreateModal(false);
      // Reset form
      setCreateFormData({
        name: '',
        phoneNumber: '',
        email: '',
        propertyType: 'condo',
        budgetRange: '',
        platform: 'Walk-in',
        projectType: 'Full Renovation'
      });
      fetchAssignments(); // Refresh the list
    } catch (error) {
      console.error('Failed to create lead:', error);
      toast.error(error.response?.data?.error || 'Failed to create lead');
    }
  }

  // Update the selected lead when it changes
  async function handleLeadUpdate() {
    if (selectedLead) {
      try {
        const response = await api.get(`/leads/${selectedLead.id}`);
        const updatedLead = response.data.lead;

        // Update the selectedLead state
        setSelectedLead(updatedLead);

        // Update in the leads list
        setLeads(prevLeads => prevLeads.map(lead =>
          lead.id === updatedLead.id ? updatedLead : lead
        ));
      } catch (error) {
        console.error('Failed to fetch updated lead:', error);
        // Fall back to full refresh
        fetchAssignments();
      }
    } else {
      fetchAssignments();
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <>
      {/* Tab Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-display font-bold text-gray-900">My Leads</h2>
          <p className="text-gray-600 text-sm">
            Manage your assigned interior design & renovation leads
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* Create Lead Button */}
          <Button
            variant="primary"
            onClick={() => setShowCreateModal(true)}
            className="bg-blue-600 hover:bg-blue-700 border-blue-600"
          >
            <svg className="w-5 h-5 mr-2 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
            </svg>
            Create Lead
          </Button>

          {/* Direct Booking Button */}
          <Button
            variant="primary"
            onClick={() => navigate('/sales-forms/new')}
            className="bg-green-600 hover:bg-green-700 border-green-600"
          >
            <svg className="w-5 h-5 mr-2 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            Direct Booking
          </Button>

          {/* Desktop View Toggle */}
          <div className="hidden md:flex items-center gap-2 bg-white rounded-lg p-1 shadow-sm">
            <button
              onClick={() => setViewMode('table')}
              className={`view-toggle-btn ${viewMode === 'table' ? 'view-toggle-btn-active' : 'view-toggle-btn-inactive'}`}
              title="Table View"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z"/>
              </svg>
            </button>
            <button
              onClick={() => setViewMode('cards')}
              className={`view-toggle-btn ${viewMode === 'cards' ? 'view-toggle-btn-active' : 'view-toggle-btn-inactive'}`}
              title="Card View"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path d="M3 4a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 12a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H4a1 1 0 01-1-1v-4zM11 4a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1V4zM11 12a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4z"/>
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
        <div className="card">
          <p className="text-gray-600 text-sm mb-1">Total Leads</p>
          <p className="text-3xl font-bold text-tenq-gold">{leads.length}</p>
        </div>
        <div className="card">
          <p className="text-gray-600 text-sm mb-1">New Leads</p>
          <p className="text-3xl font-bold text-blue-600">
            {leads.filter(l => l.assignments[0]?.status === 'new').length}
          </p>
        </div>
        <div className="card">
          <p className="text-gray-600 text-sm mb-1">In Progress</p>
          <p className="text-3xl font-bold text-orange-600">
            {leads.filter(l => {
              const status = l.assignments[0]?.status;
              return ['contact_attempted', 'contact_made', 'appt_set',
                      'appt_no_show', 'appt_held', 'pending_booking'].includes(status);
            }).length}
          </p>
        </div>
        <div className="card">
          <p className="text-gray-600 text-sm mb-1">Booked</p>
          <p className="text-3xl font-bold text-green-600">
            {leads.filter(l => ['booked', 'form_submitted'].includes(l.assignments[0]?.status)).length}
          </p>
        </div>
      </div>

      {/* Enhanced Filters */}
      <LeadFilters
        filters={filters}
        onFilterChange={setFilters}
        showAgentFilter={false}
      />

      {/* Leads Display */}
      <div className="card">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-xl font-display font-bold">
            My Assigned Leads ({leads.length})
          </h2>
          <div className="text-sm text-gray-600">
            Sorted by: <span className="font-medium capitalize">
              {sortBy === 'createdAt' ? 'Date' : sortBy === 'heat' ? 'Heat Level' : sortBy === 'lastActivity' ? 'Last Activity' : sortBy}
            </span>
            <span className="ml-2">({sortOrder === 'ASC' ? 'Oldest First' : 'Newest First'})</span>
          </div>
        </div>

        {leads.length === 0 ? (
          <div className="text-center py-12">
            <svg className="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
            </svg>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No leads found</h3>
            <p className="text-gray-600">
              {filters.search || filters.status || filters.heatLevel
                ? 'Try adjusting your filters'
                : 'You have no assigned leads yet'}
            </p>
          </div>
        ) : (
          <>
            {/* Desktop Table View */}
            {viewMode === 'table' && (
              <div className="hidden md:block">
                <LeadTable
                  leads={leads}
                  onView={handleViewLead}
                  onEdit={() => {}} // Agents typically don't edit lead details
                  onDelete={() => {}} // Agents typically don't delete leads
                  sortBy={sortBy}
                  sortOrder={sortOrder}
                  onSort={handleSort}
                  onHeatLevelChange={handleHeatLevelChange}
                  hideActions={true} // Hide edit/delete for agents
                />
              </div>
            )}

            {/* Desktop Card View */}
            {viewMode === 'cards' && (
              <div className="hidden md:block">
                <LeadCardView
                  leads={leads}
                  onLeadClick={handleViewLead}
                  onHeatLevelChange={handleHeatLevelChange}
                />
              </div>
            )}

            {/* Mobile always shows cards */}
            <div className="md:hidden">
              <LeadCardView
                leads={leads}
                onLeadClick={handleViewLead}
                onHeatLevelChange={handleHeatLevelChange}
              />
            </div>
          </>
        )}
      </div>

      {/* Lead Detail Pane */}
      <LeadDetailPane
        lead={selectedLead}
        isOpen={showDetailPane}
        onClose={() => setShowDetailPane(false)}
        onEdit={handleEditLead}
        onUpdate={handleLeadUpdate}
      />

      {/* Edit Lead Modal */}
      <Modal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setEditingLead(null);
        }}
        title="Edit Lead"
        size="large"
      >
        <div className="space-y-6 max-h-[70vh] overflow-y-auto">
          {/* Contact Information */}
          <div className="border-b pb-4">
            <h3 className="font-semibold text-lg mb-3">Contact Information</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="label">Client Name</label>
                <input
                  type="text"
                  value={editFormData.name}
                  onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                  className="input w-full"
                  placeholder="John Doe"
                />
              </div>

              <div>
                <label className="label">Phone Number</label>
                <input
                  type="tel"
                  value={editFormData.phoneNumber}
                  onChange={(e) => setEditFormData({ ...editFormData, phoneNumber: e.target.value })}
                  className="input w-full"
                  placeholder="+60123456789"
                />
              </div>

              <div className="col-span-2">
                <label className="label">Email</label>
                <input
                  type="email"
                  value={editFormData.email}
                  onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })}
                  className="input w-full"
                  placeholder="john@example.com"
                />
              </div>
            </div>
          </div>

          {/* Property Details */}
          <div className="border-b pb-4">
            <h3 className="font-semibold text-lg mb-3">Property Details</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className="label">Property Address</label>
                <textarea
                  value={editFormData.address}
                  onChange={(e) => setEditFormData({ ...editFormData, address: e.target.value })}
                  className="input w-full"
                  rows="2"
                  placeholder="123 Jalan Ampang, Kuala Lumpur, 50450"
                />
              </div>

              <div>
                <label className="label">Property Type</label>
                <select
                  value={editFormData.propertyType}
                  onChange={(e) => setEditFormData({ ...editFormData, propertyType: e.target.value })}
                  className="input w-full"
                >
                  <option value="">Select type</option>
                  <option value="hdb">HDB</option>
                  <option value="condo">Condominium</option>
                  <option value="landed">Landed Property</option>
                  <option value="commercial">Commercial</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div>
                <label className="label">Room Count</label>
                <input
                  type="text"
                  value={editFormData.roomCount}
                  onChange={(e) => setEditFormData({ ...editFormData, roomCount: e.target.value })}
                  className="input w-full"
                  placeholder="3-room, 4-room, 5-room"
                />
              </div>

              <div>
                <label className="label">Square Footage</label>
                <input
                  type="number"
                  value={editFormData.squareFootage}
                  onChange={(e) => setEditFormData({ ...editFormData, squareFootage: e.target.value })}
                  className="input w-full"
                  placeholder="1200"
                  step="0.01"
                />
              </div>

              <div>
                <label className="label">Room Types</label>
                <input
                  type="text"
                  value={editFormData.roomTypes}
                  onChange={(e) => setEditFormData({ ...editFormData, roomTypes: e.target.value })}
                  className="input w-full"
                  placeholder="Living room, Kitchen, 2 Bedrooms"
                />
              </div>
            </div>
          </div>

          {/* Project Information */}
          <div className="border-b pb-4">
            <h3 className="font-semibold text-lg mb-3">Project Information</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="label">Project Type</label>
                <select
                  value={editFormData.projectType}
                  onChange={(e) => setEditFormData({ ...editFormData, projectType: e.target.value })}
                  className="input w-full"
                >
                  <option value="">Select type</option>
                  <option value="full_renovation">Full Renovation</option>
                  <option value="partial_renovation">Partial Renovation</option>
                  <option value="interior_design">Interior Design Only</option>
                  <option value="consultation">Consultation</option>
                </select>
              </div>

              <div>
                <label className="label">Preferred Style</label>
                <input
                  type="text"
                  value={editFormData.preferredStyle}
                  onChange={(e) => setEditFormData({ ...editFormData, preferredStyle: e.target.value })}
                  className="input w-full"
                  placeholder="Modern, Scandinavian, Industrial"
                />
              </div>

              <div>
                <label className="label">Timeframe</label>
                <input
                  type="text"
                  value={editFormData.timeframe}
                  onChange={(e) => setEditFormData({ ...editFormData, timeframe: e.target.value })}
                  className="input w-full"
                  placeholder="Within 3 months, 6+ months"
                />
              </div>
            </div>
          </div>

          {/* Budget */}
          <div className="border-b pb-4">
            <h3 className="font-semibold text-lg mb-3">Budget</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="label">Budget Min (RM)</label>
                <input
                  type="number"
                  value={editFormData.budgetMin}
                  onChange={(e) => setEditFormData({ ...editFormData, budgetMin: e.target.value })}
                  className="input w-full"
                  placeholder="40000"
                  step="0.01"
                />
              </div>

              <div>
                <label className="label">Budget Max (RM)</label>
                <input
                  type="number"
                  value={editFormData.budgetMax}
                  onChange={(e) => setEditFormData({ ...editFormData, budgetMax: e.target.value })}
                  className="input w-full"
                  placeholder="60000"
                  step="0.01"
                />
              </div>
            </div>
          </div>

          {/* Lead Source & Marketing */}
          <div className="border-b pb-4">
            <h3 className="font-semibold text-lg mb-3">Lead Source & Marketing</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="label">Platform</label>
                <select
                  value={editFormData.platform}
                  onChange={(e) => setEditFormData({ ...editFormData, platform: e.target.value })}
                  className="input w-full"
                >
                  <option value="">Select source</option>
                  <option value="facebook">Facebook</option>
                  <option value="instagram">Instagram</option>
                  <option value="tiktok">TikTok</option>
                  <option value="google">Google</option>
                  <option value="referral">Referral</option>
                  <option value="manual">Manual/Walk-in</option>
                </select>
              </div>

              <div>
                <label className="label">Campaign Name</label>
                <input
                  type="text"
                  value={editFormData.campaign_name}
                  onChange={(e) => setEditFormData({ ...editFormData, campaign_name: e.target.value })}
                  className="input w-full"
                  placeholder="Summer Campaign 2024"
                />
              </div>

              <div>
                <label className="label">Ad Set Name</label>
                <input
                  type="text"
                  value={editFormData.adset_name}
                  onChange={(e) => setEditFormData({ ...editFormData, adset_name: e.target.value })}
                  className="input w-full"
                  placeholder="Condo Owners 25-45"
                />
              </div>

              <div>
                <label className="label">Ad Name</label>
                <input
                  type="text"
                  value={editFormData.ad_name}
                  onChange={(e) => setEditFormData({ ...editFormData, ad_name: e.target.value })}
                  className="input w-full"
                  placeholder="Modern Kitchen Ad 1"
                />
              </div>
            </div>
          </div>

          {/* Notes */}
          <div className="pb-4">
            <h3 className="font-semibold text-lg mb-3">Notes</h3>
            <div>
              <label className="label">Initial Notes</label>
              <textarea
                value={editFormData.initialNotes}
                onChange={(e) => setEditFormData({ ...editFormData, initialNotes: e.target.value })}
                className="input w-full"
                rows="3"
                placeholder="Any additional notes about this lead..."
              />
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end gap-3 pt-4 border-t sticky bottom-0 bg-white">
            <Button
              variant="outline"
              onClick={() => {
                setShowEditModal(false);
                setEditingLead(null);
              }}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSaveEdit}
            >
              Save Changes
            </Button>
          </div>
        </div>
      </Modal>

      {/* Create Lead Modal */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => {
          setShowCreateModal(false);
          setCreateFormData({
            name: '',
            phoneNumber: '',
            email: '',
            propertyType: 'condo',
            budgetRange: '',
            platform: 'Walk-in',
            projectType: 'Full Renovation'
          });
        }}
        title="Create New Lead"
      >
        <div className="space-y-4">
          <div>
            <label className="label">
              Client Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={createFormData.name}
              onChange={(e) => setCreateFormData({ ...createFormData, name: e.target.value })}
              className="input w-full"
              placeholder="John Doe"
              required
            />
          </div>

          <div>
            <label className="label">
              Phone Number <span className="text-red-500">*</span>
            </label>
            <input
              type="tel"
              value={createFormData.phoneNumber}
              onChange={(e) => setCreateFormData({ ...createFormData, phoneNumber: e.target.value })}
              className="input w-full"
              placeholder="+60123456789"
              required
            />
          </div>

          <div>
            <label className="label">Email</label>
            <input
              type="email"
              value={createFormData.email}
              onChange={(e) => setCreateFormData({ ...createFormData, email: e.target.value })}
              className="input w-full"
              placeholder="john@example.com"
            />
          </div>

          <div>
            <label className="label">Property Type</label>
            <select
              value={createFormData.propertyType}
              onChange={(e) => setCreateFormData({ ...createFormData, propertyType: e.target.value })}
              className="input w-full"
            >
              <option value="hdb">HDB</option>
              <option value="condo">Condominium</option>
              <option value="landed">Landed Property</option>
              <option value="commercial">Commercial</option>
              <option value="other">Other</option>
            </select>
          </div>

          <div>
            <label className="label">Budget Range</label>
            <input
              type="text"
              value={createFormData.budgetRange}
              onChange={(e) => setCreateFormData({ ...createFormData, budgetRange: e.target.value })}
              className="input w-full"
              placeholder="40000-60000"
            />
            <p className="text-xs text-gray-500 mt-1">Format: min-max (e.g., 40000-60000)</p>
          </div>

          <div>
            <label className="label">Lead Source / Platform</label>
            <select
              value={createFormData.platform}
              onChange={(e) => setCreateFormData({ ...createFormData, platform: e.target.value })}
              className="input w-full"
            >
              <option value="Walk-in">Walk-in</option>
              <option value="Referral">Referral</option>
              <option value="Facebook">Facebook</option>
              <option value="Instagram">Instagram</option>
              <option value="Website">Website</option>
              <option value="Google">Google</option>
              <option value="Other">Other</option>
            </select>
          </div>

          <div>
            <label className="label">Project Type</label>
            <select
              value={createFormData.projectType}
              onChange={(e) => setCreateFormData({ ...createFormData, projectType: e.target.value })}
              className="input w-full"
            >
              <option value="Full Renovation">Full Renovation</option>
              <option value="Partial Renovation">Partial Renovation</option>
              <option value="Interior Design Only">Interior Design Only</option>
              <option value="Consultation">Consultation</option>
              <option value="Other">Other</option>
            </select>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button
              variant="outline"
              onClick={() => {
                setShowCreateModal(false);
                setCreateFormData({
                  name: '',
                  phoneNumber: '',
                  email: '',
                  propertyType: 'condo',
                  budgetRange: '',
                  platform: 'Walk-in',
                  projectType: 'Full Renovation'
                });
              }}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleCreateLead}
            >
              Create Lead
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
}
